/**
 * BatchMatrixConfig - Intuitive list-based Matrix Mode UI
 *
 * Users can add rows freely, each row is a complete configuration.
 */

import { useMemo, useRef } from 'react';
import { Plus, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BatchMatrixRow } from './BatchMatrixRow';
import { GlobalSettingsCard } from './GlobalSettingsCard';
import { ApplyGlobalConfirmDialog } from './ApplyGlobalConfirmDialog';
import { MatrixCostSummary } from './MatrixCostSummary';
import { createMatrixItem } from '../matrix-utils';
import type { GlobalMatrixSettings } from '../matrix-utils';
import type { MatrixItem } from '@/lib/api/batch';

interface Template {
  id: string;
  name: string;
}

interface Collection {
  id: string;
  name: string;
}

interface BatchMatrixConfigProps {
  items: MatrixItem[];
  templates: Template[];
  collections: Collection[];
  costPerImage: number;
  costPerImage4k: number;
  discountPercent: number;
  onChange: (items: MatrixItem[]) => void;
  onOpenGallery: (rowId: string) => void;
  onDuplicateRow: (item: MatrixItem) => void;
  disabled?: boolean;
  globalMatrixSettings: GlobalMatrixSettings;
  onGlobalSettingsChange: (key: string, value: string | number | boolean | string[]) => void;
  onApplyGlobalToAll: () => void;
  showApplyConfirmation: boolean;
  onConfirmApply: () => void;
  onCancelApply: () => void;
  getItemGenerations: (item: MatrixItem) => number;
  collectionCountCache: Map<string, number>;
  cacheCollectionCount: (id: string, count: number) => void;
}

export function BatchMatrixConfig({
  items,
  templates,
  collections,
  costPerImage,
  costPerImage4k,
  discountPercent,
  onChange,
  onOpenGallery,
  onDuplicateRow,
  disabled = false,
  globalMatrixSettings,
  onGlobalSettingsChange,
  onApplyGlobalToAll,
  showApplyConfirmation,
  onConfirmApply,
  onCancelApply,
  getItemGenerations,
  collectionCountCache,
  cacheCollectionCount,
}: BatchMatrixConfigProps) {
  const { t } = useTranslation();
  const fileInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const totalGenerations = useMemo(() => {
    return items.reduce((sum, item) => sum + getItemGenerations(item), 0);
  }, [items, getItemGenerations]);

  const hasWarning = totalGenerations > 50;
  const hasError = totalGenerations > 200;

  const handleAddRow = () => {
    onChange([...items, createMatrixItem(templates[0]?.id, globalMatrixSettings)]);
  };

  const handleRowChange = (index: number, updated: MatrixItem) => {
    const newItems = [...items];
    newItems[index] = updated;
    onChange(newItems);
  };

  const handleRowRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleFileSelected = (rowId: string, file: File) => {
    const index = items.findIndex((i) => i.id === rowId);
    if (index === -1) return;
    const thumbnailUrl = URL.createObjectURL(file);
    handleRowChange(index, {
      ...items[index],
      input_type: 'upload',
      _file: file,
      _thumbnailUrl: thumbnailUrl,
      image_id: undefined,
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Matrix Konfiguration ({items.length} Zeilen)</span>
        <span className="text-sm text-neutral-500">{totalGenerations} Bilder</span>
      </div>

      <GlobalSettingsCard
        globalSettings={globalMatrixSettings}
        templates={templates}
        collections={collections}
        onSettingsChange={onGlobalSettingsChange}
        onApplyToAll={onApplyGlobalToAll}
        totalRowsCount={items.length}
        disabled={disabled}
      />

      {/* Warning banners */}
      {hasWarning && !hasError && (
        <div className="px-2 py-1.5 rounded bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 text-xs flex items-center gap-1.5 text-yellow-700 dark:text-yellow-300">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span>{t('batch.largeBatch', { count: totalGenerations })}</span>
        </div>
      )}
      {hasError && (
        <div className="px-2 py-1.5 rounded bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-xs flex items-center gap-1.5 text-red-700 dark:text-red-300">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span>{t('batch.maxExceeded', { count: totalGenerations })}</span>
        </div>
      )}

      {/* Rows list */}
      <div className="space-y-1.5">
        {items.map((item, index) => (
          <BatchMatrixRow
            key={item.id}
            item={item}
            index={index}
            templates={templates}
            collections={collections}
            collectionCountCache={collectionCountCache}
            cacheCollectionCount={cacheCollectionCount}
            onChange={(updated) => handleRowChange(index, updated)}
            onRemove={() => handleRowRemove(index)}
            onDuplicateRow={() => onDuplicateRow(item)}
            onSelectFile={() => {
              const input = fileInputRefs.current.get(item.id);
              if (input) input.click();
            }}
            onSelectFromGallery={() => onOpenGallery(item.id)}
            canRemove={items.length > 1}
            disabled={disabled}
          />
        ))}
        {items.map((item) => (
          <input key={`file-${item.id}`} ref={(el) => { if (el) fileInputRefs.current.set(item.id, el); }}
            type="file" accept="image/*" className="hidden"
            onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileSelected(item.id, file); e.target.value = ''; }} />
        ))}
      </div>

      {/* Add row button */}
      <button
        type="button"
        onClick={handleAddRow}
        disabled={disabled || hasError}
        className="w-full py-2 rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-600 text-neutral-500 hover:border-orange-400 hover:text-orange-500 transition-colors text-xs font-medium flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('batch.addRow')}
      </button>

      <MatrixCostSummary
        items={items}
        totalGenerations={totalGenerations}
        costPerImage={costPerImage}
        costPerImage4k={costPerImage4k}
        discountPercent={discountPercent}
        getItemGenerations={getItemGenerations}
      />

      <ApplyGlobalConfirmDialog
        open={showApplyConfirmation}
        onConfirm={onConfirmApply}
        onCancel={onCancelApply}
        affectedRowsCount={items.length}
      />
    </div>
  );
}
